import PropTypes from 'prop-types';
import React from 'react';
import shortId from 'shortid';
import './Placeholder.css';

const Placeholder = React.forwardRef(({ title, viewBox, children }, ref) => {
  const id = shortId.generate();
  const idAria = `${id}-aria`;
  const idClip = `${id}-clip`;
  const idAnim = `${id}-anim`;

  return (
    <svg
      className="placeholder"
      role="img"
      ref={ref}
      viewBox={viewBox}
      aria-labelledby={idAria}
      xmlns="http://www.w3.org/2000/svg">
      <title id={idAria}>{title}</title>

      <rect
        x="0"
        y="0"
        width="100%"
        height="100%"
        role="presentation"
        clipPath={`url(#${idClip})`}
        style={{ fill: `url(#${idAnim})` }}
      />

      <defs role="presentation">
        <clipPath id={idClip}>{children && children}</clipPath>

        <linearGradient id={idAnim}>
          <stop offset="0%">
            <animate
              dur="1.5s"
              values="-2; -2; 1"
              keyTimes="0; 0.25; 1"
              attributeName="offset"
              repeatCount="indefinite"
            />
          </stop>
          <stop offset="50%">
            <animate
              dur="1.5s"
              values="-1; -1; 2"
              keyTimes="0; 0.25; 1"
              attributeName="offset"
              repeatCount="indefinite"
            />
          </stop>
          <stop offset="100%">
            <animate
              dur="1.5s"
              values="0; 0; 3"
              keyTimes="0; 0.25; 1"
              attributeName="offset"
              repeatCount="indefinite"
            />
          </stop>
        </linearGradient>
      </defs>
    </svg>
  );
});

Placeholder.propTypes = {
  title: PropTypes.string,
  viewBox: PropTypes.string
};

Placeholder.defaultProps = {
  title: 'Loading, please wait ...',
  viewBox: '0 0 600 100'
};

export default Placeholder;

const PlaceholderBulletList = React.forwardRef(({ title }, ref) => (
  <Placeholder ref={ref} title={title} viewBox="0 0 245 125">
    <circle cx="10" cy="20" r="8" />
    <rect x="25" y="15" rx="5" ry="5" width="220" height="10" />
    <circle cx="10" cy="50" r="8" />
    <rect x="25" y="45" rx="5" ry="5" width="220" height="10" />
    <circle cx="10" cy="80" r="8" />
    <rect x="25" y="75" rx="5" ry="5" width="220" height="10" />
    <circle cx="10" cy="110" r="8" />
    <rect x="25" y="105" rx="5" ry="5" width="220" height="10" />
  </Placeholder>
));

const PlaceholderCode = React.forwardRef(({ title }, ref) => (
  <Placeholder ref={ref} title={title} viewBox="0 0 340 84">
    <rect x="0" y="0" width="67" height="11" rx="3" />
    <rect x="76" y="0" width="140" height="11" rx="3" />
    <rect x="127" y="48" width="53" height="11" rx="3" />
    <rect x="187" y="48" width="72" height="11" rx="3" />
    <rect x="18" y="48" width="100" height="11" rx="3" />
    <rect x="0" y="71" width="37" height="11" rx="3" />
    <rect x="18" y="23" width="140" height="11" rx="3" />
    <rect x="166" y="23" width="173" height="11" rx="3" />
  </Placeholder>
));

const PlaceholderList = React.forwardRef(({ title }, ref) => (
  <Placeholder ref={ref} title={title} viewBox="0 0 250 35">
    <rect x="0" y="0" rx="2.5" ry="2.5" width="5" height="5" />
    <rect x="7.5" y="0" rx="2.5" ry="2.5" width="190" height="5" />
    <rect x="200" y="0" rx="2.5" ry="2.5" width="50" height="5" />

    <rect x="0" y="7.5" rx="2.5" ry="2.5" width="5" height="5" />
    <rect x="7.5" y="7.5" rx="2.5" ry="2.5" width="190" height="5" />
    <rect x="200" y="7.5" rx="2.5" ry="2.5" width="50" height="5" />

    <rect x="0" y="15" rx="2.5" ry="2.5" width="5" height="5" />
    <rect x="7.5" y="15" rx="2.5" ry="2.5" width="190" height="5" />
    <rect x="200" y="15" rx="2.5" ry="2.5" width="50" height="5" />

    <rect x="0" y="22.5" rx="2.5" ry="2.5" width="5" height="5" />
    <rect x="7.5" y="22.5" rx="2.5" ry="2.5" width="190" height="5" />
    <rect x="200" y="22.5" rx="2.5" ry="2.5" width="50" height="5" />

    <rect x="0" y="30" rx="2.5" ry="2.5" width="5" height="5" />
    <rect x="7.5" y="30" rx="2.5" ry="2.5" width="190" height="5" />
    <rect x="200" y="30" rx="2.5" ry="2.5" width="50" height="5" />
  </Placeholder>
));

export { PlaceholderBulletList, PlaceholderCode, PlaceholderList };
